// FieldVision - KortCard.tsx
// Kort-card med thumbnail, dato, kilde og zone panel
// Erstatter MapCard.tsx med mockup-design
// FIX: Tilfojet parsing af microservice FeatureCollection format for satellit-kort
// NEW: Tre-prikker menu med slet-funktion
// UPDATE: Alle kort bruger nu soft delete (kan gendannes)

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ZonePanel from './ZonePanel';
import { ZoneData, ZONE_COLORS } from './ZoneRow';

interface FieldMap {
  Id: number;
  FieldId: number | null;
  OrderId: number | null;
  Type: string;
  Source: string;
  MapFormat?: string;  // Terminal format kortet er uploadet som (drone-kort)
  CaptureDate: string;
  Status: string;
  ZonesJson?: string;
  Zones?: any;
  ThumbnailPath?: string;
  CreatedAt: string;
  Marknr: string;
  Areal: number | null;
  CVR: string;
}

interface DeleteResponse {
  success: boolean;
  deleteType: 'hard' | 'soft';
  message: string;
}

interface KortCardProps {
  map: FieldMap;
  isActive: boolean;
  onSelect: () => void;
  onZonesChange?: (zones: ZoneData[]) => void;
  onDelete?: (mapId: number, deleteType: 'hard' | 'soft') => void;
}

/**
 * Helper function to parse microservice FeatureCollection format.
 * Each feature represents a zone with properties: zone, hectares, color, etc.
 */
const parseFeatureCollectionZones = (fc: any): ZoneData[] | null => {
  if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
    return null;
  }

  // Check if features have zone property (microservice format)
  const hasZoneProperty = fc.features.some((f: any) =>
    f.properties && typeof f.properties.zone === 'number'
  );

  if (!hasZoneProperty) {
    return null;
  }

  return fc.features
    .filter((f: any) => f.properties && typeof f.properties.zone === 'number')
    .map((f: any) => ({
      zone: f.properties.zone,
      rate: f.properties.rate || 0,
      hectares: f.properties.hectares || 0,
      color: f.properties.color || ZONE_COLORS[f.properties.zone],
      ndreMin: f.properties.ndreMin,
      ndreMax: f.properties.ndreMax,
      description: f.properties.description,
    }));
};

const KortCard: React.FC<KortCardProps> = ({
  map,
  isActive,
  onSelect,
  onZonesChange,
  onDelete
}) => {
  const [zones, setZones] = useState<ZoneData[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Parse zones fra map data
  useEffect(() => {
    let parsedZones: ZoneData[] = [];
    let detectedFormat = 'unknown';

    if (map.Zones) {
      if (Array.isArray(map.Zones)) {
        // Format 1: Array direkte
        parsedZones = map.Zones;
        detectedFormat = 'array';
      } else if (map.Zones.zones) {
        // Format 2: { zones: [...] }
        parsedZones = map.Zones.zones;
        detectedFormat = 'zones-object';
      } else if (map.Zones.type === 'FeatureCollection' && Array.isArray(map.Zones.features)) {
        // Format 3: Microservice FeatureCollection (satellit) - NY!
        // Hver feature er en zone med properties.zone
        const fcZones = parseFeatureCollectionZones(map.Zones);
        if (fcZones && fcZones.length > 0) {
          parsedZones = fcZones;
          detectedFormat = 'microservice-featurecollection';
        }
      }

      // Format 4: Drone format med geojson.features - BEVAR UAENDRET!
      if (parsedZones.length === 0 && map.Zones.geojson?.features) {
        parsedZones = map.Zones.geojson.features.map((f: any) => ({
          zone: f.properties.zone,
          rate: f.properties.rate,
          rateInt: f.properties.rateInt,
          unit: f.properties.unit || 'l/ha',
          hectares: f.properties.hectares,
        }));
        detectedFormat = 'drone-geojson';
      }
    } else if (map.ZonesJson) {
      try {
        const parsed = JSON.parse(map.ZonesJson);

        if (Array.isArray(parsed)) {
          // Format 1: Array direkte
          parsedZones = parsed;
          detectedFormat = 'zonesJson-array';
        } else if (parsed.zones) {
          // Format 2: { zones: [...] }
          parsedZones = parsed.zones;
          detectedFormat = 'zonesJson-zones-object';
        } else if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
          // Format 3: Microservice FeatureCollection (satellit) - NY!
          const fcZones = parseFeatureCollectionZones(parsed);
          if (fcZones && fcZones.length > 0) {
            parsedZones = fcZones;
            detectedFormat = 'zonesJson-microservice-featurecollection';
          }
        }

        // Format 4: Drone format med geojson.features - BEVAR UAENDRET!
        if (parsedZones.length === 0 && parsed.geojson?.features) {
          parsedZones = parsed.geojson.features.map((f: any) => ({
            zone: f.properties.zone,
            rate: f.properties.rate,
            rateInt: f.properties.rateInt,
            unit: f.properties.unit || 'l/ha',
            hectares: f.properties.hectares,
          }));
          detectedFormat = 'zonesJson-drone-geojson';
        }
      } catch (err) {
        console.error('Error parsing ZonesJson:', err);
      }
    }

    setZones(parsedZones);

    // Debug log for format detection
    console.log('KortCard zones parsed:', {
      mapId: map.Id,
      source: map.Source,
      type: map.Type,
      zonesCount: parsedZones.length,
      format: detectedFormat
    });
  }, [map]);

  // Luk menu naar der klikkes udenfor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Aktive zoner (uden zone 0)
  const activeZones = useMemo(() => {
    return zones.filter(z => z.zone !== 0).sort((a, b) => a.zone - b.zone);
  }, [zones]);

  const hasZones = activeZones.length > 0;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'ndvi':
        return 'NDVI';
      case 'ndre':
        return 'NDRE';
      case 'weed':
        return 'Ukrudt';
      case 'regrowth':
        return 'Genvaekst';
      default:
        return type || 'Kort';
    }
  };

  const getSourceLabel = (source: string) => {
    if (source === 'drone') return 'Drone';
    if (source === 'satellite-microservice') return 'Satellit';
    if (source === 'satellite') return 'Satellit';
    if (source?.includes('sentinel')) return 'Satellit';
    return source || 'Ukendt';
  };

  // Haandter zone-aendringer
  const handleZonesChange = (updatedZones: ZoneData[]) => {
    setZones(updatedZones);
    if (onZonesChange) {
      onZonesChange(updatedZones);
    }
  };

  // Haandter menu toggle
  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  // Haandter slet klik
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowDeleteConfirm(true);
  };

  // Bekraeft sletning
  const confirmDelete = async () => {
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/${map.Id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke slette kort');
      }

      const result: DeleteResponse = await response.json();

      // Luk dialog
      setShowDeleteConfirm(false);

      // Informer parent om sletning
      if (onDelete) {
        onDelete(map.Id, result.deleteType);
      }
    } catch (error) {
      console.error('Error deleting map:', error);
      alert('Der opstod en fejl ved sletning af kortet. Proev igen.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Annuller sletning
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className={`kort-card ${isActive ? 'active' : ''}`}>
      {/* Card Header - klikbar for at vaelge */}
      <div className="kort-card-header-wrapper">
        <button className="kort-card-header" onClick={onSelect}>
          {/* Thumbnail med zone-farver */}
          <div className="kort-thumbnail">
            {hasZones ? (
              <svg viewBox="0 0 60 60" className="zone-thumbnail">
                {/* Baggrundsfarve */}
                <rect x="0" y="0" width="60" height="60" fill="#f3f4f6" />
                {/* Zone-segmenter som en simpel gradient-repraesentation */}
                {activeZones.map((zone, index) => {
                  const segmentHeight = 60 / activeZones.length;
                  return (
                    <rect
                      key={zone.zone}
                      x="5"
                      y={5 + index * segmentHeight}
                      width="50"
                      height={segmentHeight - 2}
                      fill={zone.color || ZONE_COLORS[zone.zone] || '#9ca3af'}
                      rx="2"
                    />
                  );
                })}
              </svg>
            ) : (
              <svg viewBox="0 0 60 60" className="zone-thumbnail">
                <rect x="0" y="0" width="60" height="60" fill="#f3f4f6" />
                <polygon
                  points="30,10 50,20 50,45 30,50 10,45 10,20"
                  fill="#e5e7eb"
                  stroke="#d1d5db"
                  strokeWidth="1"
                />
              </svg>
            )}
          </div>

          {/* Kort info */}
          <div className="kort-info">
            <div className="kort-title-row">
              <span className="kort-type">{getTypeLabel(map.Type)}</span>
              <span className="kort-source">{getSourceLabel(map.Source)}</span>
            </div>
            <div className="kort-meta">
              <span className="kort-date" title="Kort fra dato">📷 {formatDate(map.CaptureDate || map.CreatedAt)}</span>
              <span className="kort-date" title="Bestillingsdato">📅 {formatDate(map.CreatedAt)}</span>
              {hasZones && (
                <span className="zone-count">{activeZones.length} zoner</span>
              )}
            </div>
          </div>

          {/* Expand indikator */}
          <span className={`expand-icon ${isActive ? 'rotated' : ''}`}>
            &#9660;
          </span>
        </button>

        {/* Tre-prikker menu */}
        <div className="kort-menu-container" ref={menuRef}>
          <button
            className="kort-menu-btn"
            onClick={handleMenuClick}
            aria-label="Kortmenu"
            title="Flere handlinger"
          >
            &#8942;
          </button>

          {menuOpen && (
            <div className="kort-menu-dropdown">
              <button className="kort-menu-item delete" onClick={handleDeleteClick}>
                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor" style={{verticalAlign: "middle", marginRight: "6px"}}><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
                Slet
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Zone Panel - accordion content */}
      {isActive && hasZones && (
        <div className="kort-zone-panel">
          <ZonePanel
            zones={zones}
            mapId={map.Id}
            source={map.Source}
            mapFormat={map.MapFormat}
            onZonesChange={handleZonesChange}
          />
        </div>
      )}

      {/* No zones message */}
      {isActive && !hasZones && (
        <div className="no-zones-message">
          <p>Ingen zone-data tilgaengelig for dette kort.</p>
        </div>
      )}

      {/* Bekraeftelses-dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelDelete}>
          <div className="delete-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-icon">
              &#128465;
            </div>
            <h3 className="delete-confirm-title">
              Flyt til papirkurv?
            </h3>
            <p className="delete-confirm-message">
              Kortet flyttes til papirkurven og kan gendannes senere.
            </p>
            <div className="delete-confirm-actions">
              <button
                className="delete-confirm-cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Annuller
              </button>
              <button
                className="delete-confirm-btn"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Sletter...' : 'Flyt til papirkurv'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .kort-card {
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          overflow: hidden;
          background: white;
          transition: all 0.2s;
        }

        .kort-card:hover {
          border-color: #d1d5db;
        }

        .kort-card.active {
          border-color: #3c6c34;
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.15);
        }

        .kort-card-header-wrapper {
          display: flex;
          align-items: stretch;
        }

        .kort-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          flex: 1;
          background: white;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }

        .kort-card-header:hover {
          background: #f9fafb;
        }

        .kort-thumbnail {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .zone-thumbnail {
          width: 100%;
          height: 100%;
        }

        .kort-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .kort-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .kort-type {
          font-weight: 600;
          font-size: 14px;
          color: #111827;
        }

        .kort-source {
          padding: 2px 6px;
          background: #f0fdf4;
          color: #3c6c34;
          font-size: 11px;
          font-weight: 500;
          border-radius: 4px;
        }

        .kort-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          color: #6b7280;
        }

        .zone-count {
          color: #3c6c34;
          font-weight: 500;
        }

        .expand-icon {
          color: #9ca3af;
          font-size: 10px;
          transition: transform 0.2s;
        }

        .expand-icon.rotated {
          transform: rotate(180deg);
        }

        /* Tre-prikker menu */
        .kort-menu-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .kort-menu-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 100%;
          background: transparent;
          border: none;
          border-left: 1px solid #e5e7eb;
          cursor: pointer;
          font-size: 20px;
          color: #6b7280;
          transition: all 0.15s;
        }

        .kort-menu-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .kort-menu-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 4px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 100;
          min-width: 140px;
          overflow: hidden;
        }

        .kort-menu-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 14px;
          background: white;
          border: none;
          text-align: left;
          font-size: 14px;
          color: #374151;
          cursor: pointer;
          transition: background 0.15s;
        }

        .kort-menu-item:hover {
          background: #f9fafb;
        }

        .kort-menu-item.delete {
          color: #dc2626;
        }

        .kort-menu-item.delete:hover {
          background: #fef2f2;
        }

        .menu-icon {
          font-size: 16px;
        }

        .kort-zone-panel {
          border-top: 1px solid #e5e7eb;
        }

        .no-zones-message {
          padding: 20px;
          text-align: center;
          color: #6b7280;
          font-size: 13px;
          border-top: 1px solid #e5e7eb;
        }

        .no-zones-message p {
          margin: 0;
        }

        /* Bekraeftelses-dialog */
        .delete-confirm-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .delete-confirm-dialog {
          background: white;
          border-radius: 16px;
          padding: 24px;
          max-width: 380px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .delete-confirm-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .delete-confirm-title {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .delete-confirm-message {
          margin: 0 0 24px 0;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        .delete-confirm-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .delete-confirm-cancel {
          padding: 10px 20px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.15s;
        }

        .delete-confirm-cancel:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .delete-confirm-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .delete-confirm-btn {
          padding: 10px 20px;
          background: #3c6c34;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: white;
          cursor: pointer;
          transition: all 0.15s;
        }

        .delete-confirm-btn:hover {
          background: #15803d;
        }

        .delete-confirm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default KortCard;
